import {
  Component,
  computed,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  signal,
  SimpleChanges
} from '@angular/core';
import { CHART_DATA_TYPE, CHART_SERIES_TYPE } from '@equipment/services/common-series-chart.service';
import { UserPreferencesService } from '@shared/services/user-preferences.service';

// Interface definitions
interface TooltipSeries {
  key: string;
  type: CHART_SERIES_TYPE;
  translationSlug: string;
  valueSuffix: string;
  checked: boolean;
  showInChart: boolean;
  dataType: CHART_DATA_TYPE[];
}

interface Filter {
  key: string;
  value?: string;
  type: CHART_SERIES_TYPE;
  translationSlug: string;
  valueSuffix?: string;
  valueSuffixConverted?: string;
  timeUnit?: string;
  checked: boolean;
  showInChart: boolean;
  colorIndex?: number;
  color?: string;
  hide?: boolean;
  disable?: boolean;
  hideValueSuffix?: boolean;
  filterButNotDisplay?: boolean;
  dataType: CHART_DATA_TYPE[];
  yAxis?: number;
  tooltipSeries?: TooltipSeries[] | null;
  onSeries?: string | null;
  title?: string;
  shape?: string;
  useValueAsKey?: boolean;
  borderRadius?: number | null;
  fillColor?: string;
  linkTo?: string[];
  style?: { color: string };
  states?: { hover: { fillColor: string, color: string, lineColor: string } };
  width?: number;
}

export interface FilterCategory {
  key: string;
  translationSlug: string;
  checked: boolean;
  hide?: boolean;
  filters: Filter[];
}

type FiltersType = FilterCategory[];

@Component({
  selector: 'app-dynamic-filter',
  templateUrl: './dynamic-filter.component.html',
  styleUrls: ['./dynamic-filter.component.scss']
})
export class DynamicFilterComponent implements OnInit, OnChanges {

  @Input() isLoading: boolean;
  @Input() filterCategories: FiltersType = [];
  @Input() initalFilterValue: FiltersType = [];
  @Output() changedValue = new EventEmitter<any>();

  // Signal for managing filter categories state
  filterCategoriesSignal = signal<FiltersType>(this.filterCategories);

  // Computed properties for filter category counts
  readonly getAvailableFiltersInCategoryCount = computed(() => (parentIndex: number) =>
    this.filterCategoriesSignal()[parentIndex].filters.filter(f => !f.filterButNotDisplay).length
  );

  readonly getCheckedFiltersInCategoryCount = computed(() => (parentIndex: number) =>
    this.filterCategoriesSignal()[parentIndex].filters.filter(f => f.checked).length
  );

  readonly partiallyComplete = computed(() => (parentIndex: number) =>
    this.filterCategoriesSignal()[parentIndex].filters.some(f => f.checked) &&
    !this.allComplete()(parentIndex)
  );

  readonly allComplete = computed(() => (parentIndex: number) =>
    this.filterCategoriesSignal()[parentIndex].filters.every(f => f.checked)
  );

  readonly isExpanded = computed(() => (parentIndex: number) =>
    this.filterCategoriesSignal()[parentIndex].checked || this.allComplete()(parentIndex) || this.partiallyComplete()(parentIndex)
  );

  constructor() {
  }

  ngOnInit(): void {
    this.filterCategoriesSignal.set(this.filterCategories);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.filterCategories && !changes.filterCategories.firstChange) {
      this.filterCategories = changes.filterCategories.currentValue;
      this.filterCategoriesSignal.set(this.filterCategories);
    }
  }

  // Method to update the checked state of filters
  update(checked: boolean, parentIndex: number, childIndex?: number): void {
    this.filterCategoriesSignal.update((filterCategories) => {
      const filterCategory = { ...filterCategories[parentIndex] };
      if (childIndex === undefined) {
        filterCategory.checked = checked;
        filterCategory.filters.map(f => {
          if (!f.disable) {
            f.checked = checked;
          }
          f.linkTo?.map((filterStr) => {
            const filter = filterCategories
              .find((categ) => categ.key === filterStr.split('.')[0]).filters
              .find((filterr) => filterr.key === filterStr.split('.')[1]);
            filter.checked = checked;
          });
          return f;
        });
      } else {
        const filter = { ...filterCategory.filters[childIndex] };
        filter.checked = checked;
        filterCategory.filters[childIndex] = filter;
        filterCategory.checked = filterCategory.filters.every(f => f.checked);
        filter.linkTo?.map((filterStr) => {
          const filterr = filterCategories
            .find((categ) => categ.key === filterStr.split('.')[0]).filters
            .find((filtreItem) => filtreItem.key === filterStr.split('.')[1]);
          filterr.checked = checked;
        });
      }

      filterCategories[parentIndex] = filterCategory;

      this.changedValue.emit({ changedValue: filterCategories }); // Emit updated value
      return filterCategories;
    });
  }


  resetFilters(): void {
    this.filterCategoriesSignal.set(this.initalFilterValue);
  }
}
